<template>
  <div>
    <div class="panel-heading">
      <h4 class="panel-title">
        <i class="material-icons rtl-mr-6" style="vertical-align: middle; font-size: 20px;">bar_chart</i>
        {{ $t('sidebar.statisticsDashboard') }}
      </h4>
    </div>

    <div class="panel-body" style="display: block;">
      <!-- Controls row -->
      <div class="row" style="margin-bottom: 16px; align-items: flex-end; display: flex; flex-wrap: wrap; gap: 8px;">
        <div class="col-xs-12 col-sm-6 col-md-3">
          <label style="font-size: 12px; color: #555; margin-bottom: 4px; display: block;">{{ $t('statistics.fromDateLabel') }}</label>
          <date-picker
            :date="startTime"
            :option="fromOption"
            class="form-control"
            style="border-bottom-color: #919191;"
          />
        </div>
        <div class="col-xs-12 col-sm-6 col-md-3">
          <label style="font-size: 12px; color: #555; margin-bottom: 4px; display: block;">{{ $t('statistics.toDateLabel') }}</label>
          <date-picker
            :date="endTime"
            :option="toOption"
            class="form-control"
            style="border-bottom-color: #919191;"
          />
        </div>
        <div class="col-xs-12 col-sm-6 col-md-2">
          <label style="font-size: 12px; color: #555; margin-bottom: 4px; display: block;">{{ $t('statistics.chartTypeLabel') }}</label>
          <select v-model="chartType" class="form-control" style="height: 38px;">
            <option value="bar">{{ $t('statistics.barChartOption') }}</option>
            <option value="pie">{{ $t('statistics.pieChartOption') }}</option>
            <option value="doughnut">{{ $t('statistics.doughnutChartOption') }}</option>
            <option value="line">{{ $t('statistics.lineChartOption') }}</option>
          </select>
        </div>
        <div class="col-xs-12 col-sm-6 col-md-2" style="padding-top: 20px;">
          <v-btn @click="loadData" depressed :loading="loading" color="primary" dark style="height: 38px; margin-top: 2px;">
            <v-icon left small>search</v-icon> {{ $t('statistics.applyButton') }}
          </v-btn>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center" style="padding: 60px 0;">
        <i class="fa fa-spinner fa-spin fa-3x text-muted"></i>
        <div class="text-muted" style="margin-top: 12px; font-size: 14px;">{{ $t('statistics.loadingStatisticsText') }}</div>
      </div>

      <!-- Empty state -->
      <div v-else-if="!hasData" class="text-center" style="padding: 60px 0; color: #aaa;">
        <i class="material-icons" style="font-size: 48px;">insert_chart_outlined</i>
        <div style="margin-top: 12px; font-size: 15px;">{{ $t('statistics.selectDateRangeHint') }}</div>
      </div>

      <!-- No results after filtering -->
      <div v-else-if="chartCards.length === 0" class="text-center" style="padding: 60px 0; color: #aaa;">
        <i class="material-icons" style="font-size: 48px;">search_off</i>
        <div style="margin-top: 12px; font-size: 15px;">{{ $t('statistics.noDataFoundHint') }}</div>
      </div>

      <!-- Charts grid -->
      <div v-else>
        <div class="row" style="display: flex; flex-wrap: wrap;">
          <div
            v-for="card in chartCards"
            :key="card.key"
            class="col-xs-12 col-sm-6 col-md-6"
            style="margin-bottom: 24px; display: flex; flex-direction: column;"
          >
            <div class="stat-chart-card">
              <div class="stat-chart-title">
                {{ card.title }}
                <button
                  class="stat-fullscreen-btn"
                  @click="openFullscreen(card)"
                  :title="$t('statistics.fullScreenTitle')"
                >
                  <i class="material-icons" style="font-size: 18px; vertical-align: middle;">fullscreen</i>
                </button>
              </div>
              <div style="position: relative; height: 280px;">
                <component
                  :is="chartComponents[chartType]"
                  :key="card.key + '-' + chartType"
                  :chart-data="card.chartData"
                  :options="chartOptions(card)"
                  style="height: 280px;"
                />
              </div>
              <!-- Mini-table -->
              <div style="margin-top: 10px; font-size: 12px;">
                <div
                  v-for="(item, i) in card.items.slice(0, 5)"
                  :key="i"
                  style="display: flex; justify-content: space-between; padding: 3px 8px; border-bottom: 1px solid #f0f0f0;"
                >
                  <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 75%;">
                    <span
                      :style="{ background: chartColors[i], display: 'inline-block', width: '10px', height: '10px', borderRadius: '50%', marginRight: '5px', flexShrink: '0' }"
                    ></span>
                    {{ item.label }}
                  </span>
                  <strong>{{ formatValue(item.value, card.isCurrency) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Fullscreen overlay -->
      <div v-if="fullscreenCard" class="stat-fullscreen-overlay" @click.self="closeFullscreen">
        <div class="stat-fullscreen-modal">
          <div class="stat-fullscreen-header">
            <span class="stat-fullscreen-title">{{ fullscreenCard.title }}</span>
            <button class="stat-fullscreen-close" @click="closeFullscreen">
              <i class="material-icons">close</i>
            </button>
          </div>
          <div class="stat-fullscreen-body">
            <div style="position: relative; height: 100%;">
              <component
                :is="chartComponents[chartType]"
                :key="fullscreenCard.key + '-fs-' + chartType"
                :chart-data="fullscreenCard.chartData"
                :options="chartOptions(fullscreenCard, true)"
                style="height: 100%;"
              />
            </div>
          </div>
          <div class="stat-fullscreen-table">
            <div
              v-for="(item, i) in fullscreenCard.items"
              :key="i"
              style="display: inline-flex; align-items: center; margin: 4px 12px; font-size: 13px;"
            >
              <span :style="{ background: chartColors[i % chartColors.length], display: 'inline-block', width: '11px', height: '11px', borderRadius: '50%', marginRight: '6px', flexShrink: '0' }"></span>
              <span class="rtl-mr-6">{{ item.label }}</span>
              <strong>{{ formatValue(item.value, fullscreenCard.isCurrency) }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import myDatepicker from 'vue-datepicker'
import { Bar, Pie, Doughnut, Line } from 'vue-chartjs'

// Wrapper components so we can dynamically switch chart types
const BarChart = {
  extends: Bar,
  props: ['chartData', 'options'],
  mounted () {
    this.renderChart(this.chartData, this.options)
  },
  watch: {
    chartData (newVal) {
      if (this._chart) {
        this._chart.destroy()
      }
      this.renderChart(newVal, this.options)
    }
  }
}

const PieChart = {
  extends: Pie,
  props: ['chartData', 'options'],
  mounted () {
    this.renderChart(this.chartData, this.options)
  },
  watch: {
    chartData (newVal) {
      if (this._chart) {
        this._chart.destroy()
      }
      this.renderChart(newVal, this.options)
    }
  }
}

const DoughnutChart = {
  extends: Doughnut,
  props: ['chartData', 'options'],
  mounted () {
    this.renderChart(this.chartData, this.options)
  },
  watch: {
    chartData (newVal) {
      if (this._chart) {
        this._chart.destroy()
      }
      this.renderChart(newVal, this.options)
    }
  }
}

const LineChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted () {
    this.renderChart(this.chartData, this.options)
  },
  watch: {
    chartData (newVal) {
      if (this._chart) {
        this._chart.destroy()
      }
      this.renderChart(newVal, this.options)
    }
  }
}

const CHART_COLORS = [
  '#e6194b', '#3cb44b', '#4363d8', '#f58231', '#911eb4',
  '#42d4f4', '#f032e6', '#bfef45', '#fabed4', '#469990'
]

export default {
  name: 'StatisticsDashboard',

  components: {
    'date-picker': myDatepicker,
    BarChart,
    PieChart,
    DoughnutChart,
    LineChart
  },

  data () {
    return {
      loading: false,
      hasData: false,
      chartType: 'doughnut',
      startTime: { time: '' },
      endTime: { time: '' },
      fromOption: this.makeDatepickerOption(this.$t('statistics.fromDateLabel')),
      toOption: this.makeDatepickerOption(this.$t('statistics.toDateLabel')),
      chartColors: CHART_COLORS,
      chartComponents: {
        bar: 'BarChart',
        pie: 'PieChart',
        doughnut: 'DoughnutChart',
        line: 'LineChart'
      },
      rawData: null,
      fullscreenCard: null
    }
  },

  computed: {
    chartCards () {
      if (!this.rawData) return []
      const d = this.rawData
      return [
        {
          key: 'customers_orders',
          title: this.$t('statistics.topCustomersByOrderCount'),
          items: d.top_customers_by_orders,
          isCurrency: false,
          chartData: this.buildChartData(d.top_customers_by_orders, this.$t('invoice.ordersLabel'))
        },
        {
          key: 'customers_revenue',
          title: this.$t('statistics.topCustomersByRevenue'),
          items: d.top_customers_by_revenue,
          isCurrency: true,
          chartData: this.buildChartData(d.top_customers_by_revenue, this.$t('statistics.revenueUsdLabel'))
        },
        {
          key: 'websites',
          title: this.$t('statistics.topWebsitesByOrders'),
          items: d.top_websites,
          isCurrency: false,
          chartData: this.buildChartData(d.top_websites, this.$t('invoice.ordersLabel'))
        },
        {
          key: 'warehouses',
          title: this.$t('statistics.topWarehousesByOrders'),
          items: d.top_warehouses,
          isCurrency: false,
          chartData: this.buildChartData(d.top_warehouses, this.$t('invoice.ordersLabel'))
        },
        {
          key: 'products',
          title: this.$t('statistics.topProductsByOrders'),
          items: d.top_products,
          isCurrency: false,
          chartData: this.buildChartData(d.top_products, this.$t('invoice.ordersLabel'))
        },
        {
          key: 'brands',
          title: this.$t('statistics.topBrandsByOrders'),
          items: d.top_brands,
          isCurrency: false,
          chartData: this.buildChartData(d.top_brands, this.$t('invoice.ordersLabel'))
        },
        {
          key: 'statuses',
          title: this.$t('statistics.orderStatusDistribution'),
          items: d.top_statuses,
          isCurrency: false,
          chartData: this.buildChartData(d.top_statuses, this.$t('invoice.ordersLabel'))
        },
        {
          key: 'item_types',
          title: this.$t('statistics.topItemTypesByOrders'),
          items: d.top_item_types,
          isCurrency: false,
          chartData: this.buildChartData(d.top_item_types, this.$t('invoice.ordersLabel'))
        },
        {
          key: 'monthly_trend',
          title: this.$t('statistics.monthlyOrderTrend'),
          items: d.monthly_trend,
          isCurrency: false,
          chartData: this.buildChartData(d.monthly_trend, this.$t('invoice.ordersLabel'), true)
        }
      ].filter(card => card.items && card.items.length > 0)
    }
  },

  mounted () {
    const userData = JSON.parse(localStorage.getItem('user_data') || '{}')
    if (!userData.is_staff) {
      this.$router.push('/')
      return
    }
    this.initCurrentMonth()
    this.loadData()
    window.addEventListener('keydown', this.onKeyDown)
  },

  beforeDestroy () {
    window.removeEventListener('keydown', this.onKeyDown)
    document.body.style.overflow = ''
  },

  methods: {
    initCurrentMonth () {
      const now = new Date()
      const oneMonthAgo = new Date(now)
      oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
      const pad = (n) => String(n).padStart(2, '0')
      this.startTime.time = `${oneMonthAgo.getFullYear()}-${pad(oneMonthAgo.getMonth() + 1)}-${pad(oneMonthAgo.getDate())}`
      this.endTime.time = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
    },

    makeDatepickerOption (placeholder) {
      return {
        type: 'day',
        week: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
        month: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
        format: 'YYYY-MM-DD',
        placeholder,
        inputStyle: {
          display: 'inline-block',
          padding: '6px',
          'line-height': '22px',
          'font-size': '16px',
          color: '#2d2d2d',
          width: '100%'
        },
        color: { header: '#1565c0', headerText: '#ffffff' },
        overlayOpacity: 0.5,
        dismissible: true
      }
    },

    buildChartData (items, datasetLabel, isLine) {
      const labels = (items || []).map(i => i.label || '(unknown)')
      const values = (items || []).map(i => parseFloat(i.value) || 0)

      if (isLine || this.chartType === 'line') {
        return {
          labels,
          datasets: [{
            label: datasetLabel,
            data: values,
            borderColor: CHART_COLORS[0],
            backgroundColor: 'rgba(21, 101, 192, 0.15)',
            pointBackgroundColor: CHART_COLORS[0],
            fill: true,
            tension: 0.3
          }]
        }
      }

      return {
        labels,
        datasets: [{
          label: datasetLabel,
          data: values,
          backgroundColor: CHART_COLORS.slice(0, labels.length),
          borderColor: '#fff',
          borderWidth: 2
        }]
      }
    },

    onKeyDown (e) {
      if (e.key === 'Escape') this.closeFullscreen()
    },

    openFullscreen (card) {
      this.fullscreenCard = card
      document.body.style.overflow = 'hidden'
    },

    closeFullscreen () {
      this.fullscreenCard = null
      document.body.style.overflow = ''
    },

    chartOptions (card, isFullscreen) {
      const isPieOrDonut = this.chartType === 'pie' || this.chartType === 'doughnut'
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: isPieOrDonut,
          position: 'bottom',
          labels: { fontSize: 10, boxWidth: 12, padding: 8 }
        },
        tooltips: {
          callbacks: {
            label: (item, data) => {
              const label = data.labels[item.index] || data.datasets[item.datasetIndex].label || ''
              const val = isPieOrDonut
                ? data.datasets[item.datasetIndex].data[item.index]
                : item.yLabel
              const fmt = card.isCurrency ? `$${Number(val).toLocaleString()}` : Number(val).toLocaleString()
              return ` ${label}: ${fmt}`
            }
          }
        },
        scales: isPieOrDonut
          ? {}
          : {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                callback: (v) => card.isCurrency ? `$${Number(v).toLocaleString()}` : v
              }
            }],
            xAxes: [{ ticks: { fontSize: 10 } }]
          }
      }
    },

    formatValue (val, isCurrency) {
      const n = parseFloat(val) || 0
      if (isCurrency) return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      return n.toLocaleString('en-US')
    },

    loadData () {
      if (!this.startTime.time || !this.endTime.time) {
        window.swal(this.$t('common.error'), this.$t('statistics.pleaseSelectFromToDate'), 'error')
        return
      }
      this.loading = true
      this.hasData = false
      const token = localStorage.getItem('access_token')
      axios.post('api/statistics/top10', {
        from_date: this.startTime.time,
        to_date: this.endTime.time
      }, {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then(res => {
          this.rawData = res.data.data
          this.hasData = true
        })
        .catch(() => {
          window.swal(this.$t('common.error'), this.$t('statistics.failedToLoadStatistics'), 'error')
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.stat-chart-card {
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  flex: 1;
  display: flex;
  flex-direction: column;
}

.stat-chart-title {
  font-size: 13px;
  font-weight: 700;
  color: #444;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-bottom: 2px solid #1565c0;
  padding-bottom: 8px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stat-fullscreen-btn {
  background: none;
  border: none;
  padding: 0 2px;
  cursor: pointer;
  color: #1565c0;
  line-height: 1;
  flex-shrink: 0;
}

.stat-fullscreen-btn:hover {
  color: #0d47a1;
}

.stat-fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-fullscreen-modal {
  background: #fff;
  border-radius: 10px;
  width: 92vw;
  height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 40px rgba(0,0,0,0.4);
}

.stat-fullscreen-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 2px solid #1565c0;
  flex-shrink: 0;
}

.stat-fullscreen-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-fullscreen-close {
  background: none;
  border: none;
  cursor: pointer;
  color: #555;
  padding: 2px;
  line-height: 1;
}

.stat-fullscreen-close:hover {
  color: #c62828;
}

.stat-fullscreen-body {
  flex: 1;
  padding: 20px;
  min-height: 0;
}

.stat-fullscreen-table {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  background: #fafafa;
}
</style>



// WEBPACK FOOTER //
// src/components/statisticsDashboard.vue